import type { ReactNode, ElementType } from 'react';

type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body'
  | 'body-sm'
  | 'caption'
  | 'overline';

interface TypographyProps {
  variant?: TypographyVariant;
  as?: ElementType;
  children: ReactNode;
  className?: string;
}

const variantConfig: Record<TypographyVariant, { tag: ElementType; classes: string }> = {
  h1: { tag: 'h1', classes: 'text-3xl font-bold text-neutral-900' },
  h2: { tag: 'h2', classes: 'text-2xl font-bold text-neutral-900' },
  h3: { tag: 'h3', classes: 'text-xl font-semibold text-neutral-900' },
  h4: { tag: 'h4', classes: 'text-lg font-semibold text-neutral-800' },
  h5: { tag: 'h5', classes: 'text-base font-semibold text-neutral-800' },
  h6: { tag: 'h6', classes: 'text-sm font-semibold text-neutral-800' },
  body: { tag: 'p', classes: 'text-base text-neutral-700' },
  'body-sm': { tag: 'p', classes: 'text-sm text-neutral-600' },
  caption: { tag: 'span', classes: 'text-xs text-neutral-500' },
  overline: {
    tag: 'span',
    classes: 'text-xs font-semibold uppercase tracking-wider text-neutral-500',
  },
};

export function Typography({ variant = 'body', as, children, className = '' }: TypographyProps) {
  const config = variantConfig[variant];
  const Component = as ?? config.tag;

  return <Component className={`${config.classes} ${className}`}>{children}</Component>;
}
